// UX32: image upload via nostr.build (the standard Nostr-ecosystem
// image host — Damus, Primal, Amethyst all use it). Keeps the "no
// central database" posture since the image lives on nostr.build,
// not on Verdant.
//
// Protocol: NIP-96 upload endpoint + NIP-98 HTTP auth. We build a
// kind:27235 authorization event with ["u", url] and ["method", "POST"]
// tags, sign it with the user's existing auth.sign(), base64 it, and
// pass as Authorization: Nostr <base64>.
//
// The signing step uses whichever path auth already supports — NIP-07
// extension (prompts the user), local or Google key (silent). All work.

// Endpoint sourced from nostr.build's NIP-96 service descriptor:
//   https://nostr.build/.well-known/nostr/nip96.json → api_url
// The old /api/v2/upload path (what we shipped originally) was
// decommissioned when nostr.build moved to a formal NIP-96 flow,
// which is why the upload failed with "Failed to fetch" — the dead
// endpoint drops the connection before the browser can read a
// response.
const NOSTR_BUILD_UPLOAD_URL = 'https://nostr.build/api/v2/nip96/upload'
const NIP98_KIND = 27235

export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024  // 5 MB

export async function uploadImageToNostrBuild(file, sign) {
  if (!file) throw new Error('No file selected.')
  if (!file.type || !file.type.startsWith('image/')) {
    throw new Error('That file is not an image.')
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(`Image is too large — max ${MAX_UPLOAD_BYTES / 1024 / 1024} MB.`)
  }
  if (typeof sign !== 'function') {
    throw new Error('Signing is not available for this account.')
  }

  const authTemplate = {
    kind: NIP98_KIND,
    created_at: Math.floor(Date.now() / 1000),
    tags: [
      ['u', NOSTR_BUILD_UPLOAD_URL],
      ['method', 'POST'],
    ],
    content: '',
  }

  const signed = await sign(authTemplate)
  const authHeader = `Nostr ${btoa(JSON.stringify(signed))}`

  const form = new FormData()
  form.append('file', file)

  const resp = await fetch(NOSTR_BUILD_UPLOAD_URL, {
    method: 'POST',
    headers: { Authorization: authHeader },
    body: form,
  })

  if (!resp.ok) {
    throw new Error(`Upload failed (${resp.status}).`)
  }

  let data
  try {
    data = await resp.json()
  } catch {
    throw new Error('Upload failed — unexpected response.')
  }

  if (data?.status && data.status !== 'success') {
    throw new Error(data?.message || 'Upload failed.')
  }

  // nostr.build v2 response:
  //   { status: "success", data: [{ url, nip94: { tags: [...] } }] }
  // Try the primary path first, then the NIP-94 tags as backup.
  const first = Array.isArray(data?.data) ? data.data[0] : data
  if (first?.url) return first.url
  const tags = first?.nip94?.tags || first?.nip94_event?.tags || []
  for (const tag of tags) {
    if (Array.isArray(tag) && tag[0] === 'url' && typeof tag[1] === 'string') {
      return tag[1]
    }
  }
  throw new Error('Upload succeeded but no URL returned.')
}
